/**
 * Function to freeze an object in development
 * to make it less likely I will mutate state
 * accidentally :) */
function deepFreeze<T>(object: T): T {
  if (object == null || typeof object !== 'object') {
    return object
  }

  const propNames = Object.getOwnPropertyNames(object)

  for (const name of propNames) {
    const value = (object as Record<string, unknown>)[name]

    if (value && typeof value === 'object') {
      deepFreeze(value)
    }
  }

  Object.freeze(object)
  return object
}

// The action envelope a reducer sees off the wire. The payload/error are
// genuinely `unknown` here — this is the untyped boundary the factory reads
// `type` from before dispatching to a handler.
export interface ReducerAction {
  type?: string
  payload?: unknown
  error?: unknown
}

// A single case handler: receives the current state plus the action's payload
// and error, and returns the next state.
//
// Payload/error are `any` by design, not laziness: the factory is generic over
// every slice's heterogeneous action payloads, so a handler must be free to
// annotate the exact payload it expects (e.g. `(state, post: BlogPost)`). A
// stricter `unknown` here rejects those annotations under contravariant
// parameter checks, forcing a cast in every handler. Payload safety is restored
// at the edges: typed action creators, and sagas that `put` results inferred
// from typed-redux-saga effects.
export type ReducerHandler<S> = (
  state: S,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload?: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error?: any
) => S

export type ReducerHandlers<S> = Record<string, ReducerHandler<S>>

// Slice sagas are root generator functions run by redux-saga.
export type ReducerSaga = (...args: unknown[]) => Iterator<unknown>

// The reducer this factory returns: a state reducer carrying its optional saga
// on `.saga` (read by the store's saga injector in create-store.js).
export interface ReducerWithSaga<S> {
  (state: S | undefined, action?: ReducerAction): S
  saga?: ReducerSaga
}

export default function createReducer<S>(
  initialState: S,
  handlers: ReducerHandlers<S>,
  saga?: ReducerSaga
): ReducerWithSaga<S> {
  if (process.env.NODE_ENV !== 'production') {
    deepFreeze(initialState)
  }

  const reducer: ReducerWithSaga<S> = (state = initialState, { type, payload, error } = {}) => {
    if (process.env.NODE_ENV !== 'production') {
      deepFreeze(state)
    }

    if (type && Object.prototype.hasOwnProperty.call(handlers, type)) {
      return handlers[type](state, payload, error)
    } else {
      return state
    }
  }

  reducer.saga = saga
  return reducer
}
